// @flow

import cx from 'classnames';
import * as React from 'react';
import { DocumentTitle } from 'common/components/DocumentTitle';
import FontAwesomeIcon, {
  regularAngleDown,
  regularAngleUp,
  lightSliders
} from 'common/components/FontAwesomeIcon';
import { Spinner } from 'common/components/Spinner';
import { SearchAPIActionCreators } from 'search/actions/SearchAPIActionCreators';
import searchAPI from 'search/api/searchAPI';
import { SearchFilters } from 'search/components/SearchFilters';
import { SearchResultsContainer } from 'search/components/SearchResults';
import { SearchParamsStore } from 'search/stores/ParamsStore';
import { SearchResultsStore } from 'search/stores/ResultsStore';
import { type Results, type Params } from 'search/types';
import satisfiesMediaQuery from 'utils/satisfiesMediaQuery';

const getStateFromStores = () => {
  return {
    params: SearchParamsStore.get(),
    isLoading: SearchResultsStore.isLoading(),
    results: SearchResultsStore.get(),
    activeResultId: SearchResultsStore.activeResultId()
  };
};

export type Props = {|
  initialResults: Results,
  initialParams: Params,
  eventTypes: Array<{|
    id: number,
    name: string
  |}>,
  venueTypes: {|
    featured: Array<{|
      id: number,
      name: string
    |}>,
    other: Array<{|
      id: number,
      name: string
    |}>
  |},
  venueServices: {|
    featured: Array<{|
      id: number,
      name: string
    |}>,
    other: Array<{|
      id: number,
      name: string
    |}>
  |},
  isMobile: boolean | null,
  isTablet: boolean,
  features: {|
    edit_relevance?: boolean
  |}
|};
type State = { ... };

export class SearchApp extends React.Component<Props, State> {
  isLazyLoadingMapCode: boolean;

  constructor(props: Props) {
    super(props);

    SearchAPIActionCreators.setInitialResults(props.initialResults);
    SearchAPIActionCreators.setInitialParams(props.initialParams);

    // Keep this value out of `this.state` as it is not used in `render`
    this.isLazyLoadingMapCode = false;

    this.state = {
      ...getStateFromStores(),
      isFiltersVisible: !props.isMobile,
      isSmallScreen: !!props.isMobile,
      isMapboxLoaded: false,
      MapSearch: null
    };
  }

  componentDidMount() {
    // Boot up searchAPI's History API etc. stuff
    searchAPI.initialize();

    window.addEventListener('resize', this._handleResize);
    this._handleResize();

    SearchParamsStore.addChangeListener(this._onChange);
    SearchResultsStore.addChangeListener(this._onChange);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._handleResize);
    SearchParamsStore.removeChangeListener(this._onChange);
    SearchResultsStore.removeChangeListener(this._onChange);
  }

  render(): React.Node {
    // $FlowFixMe[prop-missing] STATE
    const highlightCapacitySitting = !this.state.params.people_filter;

    const showFiltersButtonClasses = cx({
      expand: true,
      success: true,
      small: true
    });

    const showSearchFilters =
      // $FlowFixMe[prop-missing] STATE
      this.state.isFiltersVisible || !this.state.isSmallScreen;
    const openInNewTab = !this.props.isMobile && !this.props.isTablet;

    // $FlowFixMe[prop-missing] STATE
    const MapSearch = this.state.MapSearch;

    return (
      <div id="venues">
        {/* $FlowFixMe[prop-missing] STATE*/}
        <DocumentTitle title={this.state.results.meta.title} />
        {/* $FlowFixMe[prop-missing] STATE*/}
        {this.state.isSmallScreen ? (
          <div className="b-mobile-filter-container">
            <button
              className={showFiltersButtonClasses}
              onClick={this._toggleFilters}
            >
              <span>
                <FontAwesomeIcon icon={lightSliders} />
              </span>
              <span>
                {showSearchFilters
                  ? __('search.search.toggle_filters_hide')
                  : __('search.search.toggle_filters')}
              </span>
              <span>
                {showSearchFilters ? (
                  <FontAwesomeIcon icon={regularAngleUp} />
                ) : (
                  <FontAwesomeIcon icon={regularAngleDown} />
                )}
              </span>
            </button>
          </div>
        ) : null}
        {showSearchFilters && (
          <SearchFilters
            // $FlowFixMe[prop-missing] STATE
            params={this.state.params}
            eventTypes={this.props.eventTypes}
            venueTypes={this.props.venueTypes}
            venueServices={this.props.venueServices}
          />
        )}
        {/* $FlowFixMe[prop-missing] STATE*/}
        {this.state.isLoading ? (
          <div className="m-search-fixed-spinner">
            <Spinner text={__('search.search.loading')} />
          </div>
        ) : null}
        <SearchResultsContainer
          // $FlowFixMe[prop-missing] STATE
          results={this.state.results}
          // $FlowFixMe[prop-missing] STATE
          activeResultId={this.state.activeResultId}
          highlightCapacitySitting={highlightCapacitySitting}
          openInNewTab={openInNewTab}
          // $FlowFixMe[prop-missing] STATE
          params={this.state.params}
          // $FlowFixMe[prop-missing] STATE
          isLoading={this.state.isLoading}
          features={this.props.features}
        />
        {MapSearch &&
        // $FlowFixMe[prop-missing] STATE
        this.state.isMapboxLoaded &&
        // $FlowFixMe[prop-missing] STATE
        !this.state.isSmallScreen ? (
          <MapSearch
            // $FlowFixMe[prop-missing] STATE
            isLoading={this.state.isLoading}
            // $FlowFixMe[prop-missing] STATE
            results={this.state.results}
            // $FlowFixMe[prop-missing] STATE
            activeResultId={this.state.activeResultId}
          />
        ) : null}
      </div>
    );
  }

  _handleResize: () => void = () => {
    window.requestAnimationFrame(() => {
      const isSmallScreen = !satisfiesMediaQuery('topbar');

      if (!isSmallScreen) {
        this._lazyLoadMapCode();
      }

      this.setState({ isSmallScreen });
    });
  };

  _lazyLoadMapCode: () => void = () => {
    // Bail out if we're already in the process of loading the map related code
    if (this.isLazyLoadingMapCode) return;
    this.isLazyLoadingMapCode = true;

    // Use two splitpoints to try to get the mapbox to a chunk shared between search and
    // venue page, i.e. use two different `import()` calls
    // $FlowFixMe[unused-promise] -- this is a dynamic import and we don't need to handle the promise
    import('setupMapbox').then(() => {
      this.setState({
        isMapboxLoaded: true
      });
    });

    // $FlowFixMe[unused-promise] -- this is a dynamic import and we don't need to handle the promise
    import('search/components/MapSearch')
      .then(mod => mod.MapSearch)
      .then(MapSearch => {
        this.setState({
          MapSearch
        });
      });
  };

  /**
   * Event handler for 'change' events coming from the stores
   */
  _onChange: () => void = () => {
    this.setState(getStateFromStores());
  };

  _toggleFilters: () => void = () => {
    this.setState({
      // $FlowFixMe[prop-missing] STATE
      isFiltersVisible: !this.state.isFiltersVisible
    });
  };
}
